import { Constants, Workflow, SubmittalDoc, SubmittalRevision, WorkflowState, WorkflowStateComplete, SubmittalCommonUtils, SubmittalFeedItemDoc, FeedItemSubType } from 'common';

/**
 * Parsing, sequencing, and formatting of submittal identifier numbers.
 */
export class SubmittalUtils extends SubmittalCommonUtils {
  static DEFAULT_SUBMITTAL_TYPES: string[] = ['Shop Drawing', 'Product Data', 'Sample', 'Document', 'Test Report', 'Closeout', 'Other'];
  static DEFAULT_SUBMITTAL_WORKFLOW: Workflow = {
    id: 'default',
    name: 'Default Submittal Workflow',
    states: [
      {
        id: Constants.SUBMITTAL_CREATE_STATUS_ID,
        pastTense: 'Created',
        action: 'create',
        dueDays: 0,
        dueBy: [],
      },
      {
        id: Constants.SUBMITTAL_SUBMIT_STATUS_ID,
        pastTense: 'Submitted',
        action: 'Submit',
        dueDays: 10,
        dueBy: [],
      },
      {
        id: Constants.SUBMITTAL_APPROVAL_STATUS_ID,
        pastTense: 'Approved',
        action: 'Approve',
        dueDays: 5,
        dueBy: [],
      },
    ],
  };

  static TABLE_COLUMNS = [
    { key: 'number', label: 'Number' },
    { key: 'title', label: 'Title' },
    { key: 'division', label: 'Division' },
    { key: 'specSection', label: 'Spec Section' },
    { key: 'submittalType', label: 'Type' },
    { key: 'packageTitle', label: 'Package' },
    { key: 'status', label: 'Status' },
    { key: 'assignee', label: 'Assignee' },
    { key: 'statusDueDate', label: 'Assignee Due Date' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'workflowDueDate', label: 'Workflow Due Date' },
    { key: 'lateDays', label: 'Late Days' },
  ];

  static getWriteRoles(projectId: string, submittalId: string): string[] {
    return [`p-${projectId}-submittals-${submittalId}-write`, `p-${projectId}-submittals-write`, `p-${projectId}-write`];
  }

  static getReadRoles(projectId: string, submittalId: string): string[] {
    return [...this.getWriteRoles(projectId, submittalId), `p-${projectId}-submittals-${submittalId}`, `p-${projectId}-submittals`, `p-${projectId}`];
  }

  /**
   * Searches the submittals for the max number and then returns its components.
   * @param numbers Array of submittal number strings to search
   * @returns object with prefix, num, and length, e.g SUB-042 becomes { prefix: 'SUB-', num: 42, length: 3 }
   * if no numbers passed or none contained numbers, returns as if we passed 000 { prefix: '', num: 0, length: 3 }
   */
  static getMaxPattern(numbers: string[]): { prefix: string; num: number; length: number } {
    return numbers.reduce(
      (best, number) => {
        const match = number && number.match(/^(?:(.*)-)?(\d+)$/); // only match trailing digits
        if (!match) return best; // skip invalid

        const cur = { prefix: match[1] ?? '', num: parseInt(match[2], 10), length: match[2].length };

        if (!best) return cur;
        if (cur.num !== best.num) return cur.num > best.num ? cur : best;
        return cur.length > best.length ? cur : best; // tie-breaker
      },
      { prefix: '', num: 0, length: 3 } as { prefix: string; num: number; length: number },
    );
  }

  static getNextNumber(numbers: string[]): string {
    const max = this.getMaxPattern(numbers);
    return this.getNumberFromPattern(max.prefix, max.num + 1, max.length);
  }

  static getNumberFromPattern(prefix: string, num: number, padding: number): string {
    return `${prefix}${String(num).padStart(padding, '0')}`;
  }

  static getCurrentState(submittal: SubmittalDoc): WorkflowState {
    const revision = SubmittalUtils.getLatestRevision(submittal);
    return revision ? SubmittalUtils.getRevisionState(submittal, revision) : null;
  }

  static getRevisionState(submittal: SubmittalDoc, revision: SubmittalRevision): WorkflowState {
    const lastStateId = SubmittalUtils.getRevisionCompletedStateId(revision);
    // If lastStateId is null, it means we're back to the created state (first state)
    if (lastStateId === null) {
      return submittal.states[0] || null;
    }
    const prevIdx = submittal.states.findIndex(state => state.id === lastStateId);
    return prevIdx >= 0 ? submittal.states[prevIdx + 1] : null;
  }

  static getRevisionStateIndex(submittal: SubmittalDoc, revision: SubmittalRevision): number {
    const statusHistory = SubmittalUtils.getRevisionStateHistory(revision);
    if (!statusHistory || statusHistory.length === 0) {
      return 0; // No history, at first state (created)
    }

    // Find the last non-null state_id in history
    let lastCompletedStateId = null;
    let consecutiveNulls = 0;

    for (let i = statusHistory.length - 1; i >= 0; i--) {
      if (statusHistory[i].state_id === null) {
        consecutiveNulls++;
      } else {
        lastCompletedStateId = statusHistory[i].state_id;
        break;
      }
    }

    // If all entries are null, we're at first state
    if (lastCompletedStateId === null) {
      return 0;
    }

    // Find where that completed state is in the workflow
    const completedStateIndex = submittal.states.findIndex(state => state.id === lastCompletedStateId);

    // Current state = one after completed state, minus the number of backward steps (nulls)
    const currentStateIndex = completedStateIndex + 1 - consecutiveNulls;

    return Math.max(0, currentStateIndex); // Don't go below 0
  }

  static getRevisionCompletedStateId(revision: SubmittalRevision): string {
    if (!revision.status_history || revision.status_history.length === 0) return null;
    const statusHistory = SubmittalUtils.getRevisionStateHistory(revision);
    const lastStateId = statusHistory && statusHistory.length > 0 ? statusHistory[statusHistory.length - 1].state_id : null;
    // Null state_id means the submittal has been moved back to the initial "created" state
    return lastStateId ?? null;
  }

  static getRevisionPendingStates(submittal: SubmittalDoc): WorkflowState[] {
    const revision = SubmittalUtils.getLatestRevision(submittal);
    const index = this.getRevisionStateIndex(submittal, revision);
    return submittal.states.slice(index);
  }

  /**
   * Check if a submittal is in draft status (no status items in history)
   */
  static isDraft(submittal: SubmittalDoc): boolean {
    const latestRevision = SubmittalUtils.getLatestRevision(submittal);
    return !latestRevision?.status_history || !latestRevision.status_history.length;
  }

  static isApproved(submittal: SubmittalDoc): boolean {
    const latestRevision = SubmittalUtils.getLatestRevision(submittal);
    const statusHistory = SubmittalUtils.getRevisionStateHistory(latestRevision);
    const lastStatus = statusHistory[statusHistory.length - 1];
    return lastStatus?.state_id === Constants.SUBMITTAL_APPROVAL_STATUS_ID;
  }

  static renderSubmittalName(doc?: SubmittalDoc, chars?: number, fallback?: string): string {
    let fullName = '';
    if (!doc && !fallback) {
      return '';
    }

    if (!doc) {
      fullName = fallback;
    } else {
      fullName = ` #${doc.code} - ${doc.title}`;
    }

    if (chars && fullName.length > chars) {
      return `${fullName.substring(0, chars)}...`;
    }

    return fullName;
  }

  static getUnacceptedFileFeedItems(
    revision: SubmittalRevision,
    feed: SubmittalFeedItemDoc[],
  ): SubmittalFeedItemDoc[] {
    return this.getUnacceptedFeedItemsBySub(revision, feed, FeedItemSubType.FILE);
  }

  /** PHOTO feed items for this revision that are not in accepted_answers. */
  static getUnacceptedPhotoFeedItems(
    revision: SubmittalRevision,
    feed: SubmittalFeedItemDoc[],
  ): SubmittalFeedItemDoc[] {
    return this.getUnacceptedFeedItemsBySub(revision, feed, FeedItemSubType.PHOTO);
  }

  private static getUnacceptedFeedItemsBySub(
    revision: SubmittalRevision,
    feed: SubmittalFeedItemDoc[],
    sub: FeedItemSubType,
  ): SubmittalFeedItemDoc[] {
    const accepted = new Set(revision.accepted_answers || []);
    return feed.filter((item) => item.sub === sub && !item.deleted_date && !accepted.has(item._id));
  }
}
